import {
  Box,
  Divider,
  Group,
  Stack,
  TextInput,
  ThemeIcon,
  Text,
} from "@mantine/core"
import { DefaultInfiniteList } from "../../../../../-components/InfiniteList"
import { useDefaultSuspenseInfiniteQuery } from "../../../../../utils/useDefaultSuspenseInfiniteQuery"
import { useDebouncedValue } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "@tanstack/react-router"
import { ChatListItem } from "../../../../-components/chat/ChatListForCurrentUser/ChatListItem"
import { IconChevronLeft, IconStarFilled } from "@tabler/icons-react"
import { CreateChatMenu } from "@/routes/-components/chat/CreateChatMenu"
import { GetChatsParams, ChatType, UserMemberFilter } from "@/orval/gen/model"
import {
  getChats,
  getGetChatsQueryKey,
  useGetDatabaseRulesSuspense,
} from "@/orval/gen"
import { SuspenseWithFallback } from "@components/suspense"

interface Props {
  smallLayout: boolean
  subscribed: boolean
  setSubscribed: (value: boolean) => void
}

export function SearchableFeedListForUserStack({
  smallLayout,
  subscribed,
  setSubscribed,
}: Props) {
  const [searchValue, setSearchValue] = React.useState("")
  const [nameSearch] = useDebouncedValue(searchValue, 500)

  const { t } = useTranslation("app_server")

  const navigate = useNavigate()

  const params = useParams({
    from: "/app/com/feeds/$feedId/",
    shouldThrow: false,
  })

  const request: Omit<GetChatsParams, "pageSize" | "pageParam"> = {
    chatTypeFilters: [ChatType.Feed, ChatType.BroadcastFeed],
    nameSearch: nameSearch,
    userMemberFilter: subscribed
      ? UserMemberFilter.UserIsMember
      : UserMemberFilter.All,
    withUnreadCount: true,
    inactive: false,
  }

  const { data: databaseRoles } = useGetDatabaseRulesSuspense()
  const canCreateFeeds = databaseRoles.includes("CanWriteFeeds")

  return (
    <Stack>
      <Group align="end">
        <TextInput
          flex={1}
          label={t("Search")}
          value={searchValue}
          onChange={(evt) => setSearchValue(evt.currentTarget.value)}
        />
        {canCreateFeeds && (
          <CreateChatMenu
            variant="feed"
            label={smallLayout ? undefined : t("NewFeed")}
          />
        )}
      </Group>
      <Divider />
      <Box
        style={{ cursor: "pointer" }}
        onClick={() => setSubscribed(!subscribed)}
      >
        {subscribed ? (
          <Group>
            <ThemeIcon size={"sm"} c="dimmed" variant="transparent">
              <IconChevronLeft />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              {t("ShowAll")}
            </Text>
          </Group>
        ) : (
          <Group>
            <ThemeIcon size={"sm"} c="dimmed" variant="transparent">
              <IconStarFilled />
            </ThemeIcon>
            <Text variant="transparent" size="sm" c="dimmed">
              {t("ShowOnlySubscribed")}
            </Text>
          </Group>
        )}
      </Box>
      <Box>
        <ChatListItem
          entity={{
            name: t("All"),
            chatId: 0,
            chatType: "BroadcastFeed",
            isReadOnly: true,
            isInactive: false,
            chatMembers: [],
            lastMessage: { userId: 0, timeStamp: "", title: "" },
          }}
          selectionOverride={params?.feedId === "subscribed"}
          onClick={() =>
            navigate({
              to: "/app/com/feeds/$feedId",
              params: { feedId: "subscribed" },
            })
          }
        ></ChatListItem>
        <SuspenseWithFallback>
          <DefaultInfiniteList
            maw={800}
            mah={undefined}
            selectable
            style={{ overflow: "visible" }}
            queryResult={useDefaultSuspenseInfiniteQuery(
              getChats,
              (pageRequest) => ({
                ...request,
                ...pageRequest,
              }),
              {
                queryKey: getGetChatsQueryKey({ ...request }),
              }
            )}
            renderItem={({ entity: chat, selectionState }) => (
              <ChatListItem
                selectionOverride={chat.chatId.toString() === params?.feedId}
                onClick={() =>
                  navigate({
                    to: "/app/com/feeds/$feedId",
                    params: { feedId: chat.chatId.toString() },
                  })
                }
                entity={chat}
                selectionState={selectionState}
                key={chat.chatId.toString()}
              />
            )}
          />
        </SuspenseWithFallback>
      </Box>
    </Stack>
  )
}
